import { Koi } from '@ksyos/koi';
import type { TypedSchema } from '@common/validation/utils/typedSchemaType';
import { BMICategories, MeasureOfCategory, YesOrNoOrNotApplicable, YesOrNoOrOther } from '@common/enums';
import { ComorbiditiesChoice } from '@common/enums/comorbiditiesChoice';
import { watchPatSleepStudyPdfDataConsultSchema } from '@common/validation/customControls/osas/osasCloudPatExaminationSchema';
import { comorbiditiesSchema } from '@common/validation/customControls/osas/osasReferralSchemas/osasReferralSchema';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';
import type { OsasConsultCustomControlData } from './types';
import { ggzbTherCheckedTreatments } from '../../mentalHealth';
import { intoxicationValidation } from '../intoxicationSchemas';
import { allTreatmentValidationFinal } from '../osasAllTreatmentsSchemas/osasAllTreatmentsSchema';
import {
    policyHasTreatmentFieldsDraft,
    policyHasTreatmentFieldsFinal,
} from '../osasTreatmentChoice/osasTreatmentChoiceSchema';
import { adviceGeneralPractitionerNoOsaOptions } from './osasConsultReadSchema';

type TypedOsasConsultSchema = TypedSchema<OsasConsultCustomControlData>;

export const therapielandProgramSelectionValidation = {
    checkedTreatments: ggzbTherCheckedTreatments,
};
const policyHasNoTreatmentFieldsDraft = {
    callPatientAfterWeeks: Koi.number().integer().min(0).max(20).allow(null),
    messageToGeneralPractitioner: getTextAreaStringSchema(true),
    remarkNoTreatment: getTextAreaStringSchema(true),
    shouldNotifyGeneralPractitioner: Koi.boolean().allow(null),
};

export const consultDateSchema = Koi.alternatives(Koi.koi().todayOrBefore(), Koi.koi().todayOrAfter());

const policyHasNoTreatmentFieldsFinal = {
    callPatientAfterWeeks: Koi.when('hasTreatment', {
        is: false,
        otherwise: Koi.valid(null),
        then: Koi.number().integer().min(0).max(20),
    }),
    messageToGeneralPractitioner: Koi.when('shouldNotifyGeneralPractitioner', {
        is: true,
        otherwise: getTextAreaStringSchema(true),
        then: getTextAreaStringSchema(false),
    }),
    remarkNoTreatment: Koi.when('hasTreatment', {
        is: false,
        otherwise: getTextAreaStringSchema(true),
        then: getTextAreaStringSchema(false),
    }),
    shouldNotifyGeneralPractitioner: Koi.when('hasTreatment', {
        is: false,
        otherwise: Koi.valid(null),
        then: Koi.boolean(),
    }),
};

const policyNoOsaFieldsDraft = {
    adviceGeneralPractitionerNoOsa: Koi.when('hasOsa', {
        is: YesOrNoOrOther.no,
        otherwise: Koi.string().allow(null),
        then: Koi.string().valid(...adviceGeneralPractitionerNoOsaOptions, null),
    }),
    remarkAdviceGeneralPractitionerNoOsa: Koi.when('adviceGeneralPractitionerNoOsa', {
        is: 'Anders, namelijk...',
        otherwise: Koi.string().allow(null),
        then: getTextAreaStringSchema(true),
    }),
    remarkNoOsa: getTextAreaStringSchema(true),
};

const policyYesOsaFieldsDraft = {
    hasTreatment: Koi.boolean().allow(null),
    isDrivingCompetenceDiscussed: Koi.koi().enum(YesOrNoOrNotApplicable).allow(null),
    remarkIsDrivingCompetenceDiscussed: getTextAreaStringSchema(true),
    remarkOther: getTextAreaStringSchema(true),
    ...policyHasTreatmentFieldsDraft,
    ...policyHasNoTreatmentFieldsDraft,
};

const policyValidationDraft = {
    ...policyNoOsaFieldsDraft,
    ...policyYesOsaFieldsDraft,
    ...therapielandProgramSelectionValidation,
};

export const osasConsultExaminationDraft = {
    T90Percent: getTextAreaStringSchema(true),
    centralSaGreaterThan10: Koi.boolean().allow(null),
    examinationDate: Koi.string().allow(null),
    otherRelevantFindings: Koi.object({
        frequentMovements: Koi.boolean(),
        indicationOfHeartRhythmDisorder: Koi.boolean(),
        inefficientSleepHygiene: Koi.boolean(),
        other: getTextAreaStringSchema(true),
        suboptimalSleepEfficiency: Koi.boolean(),
    }),
    positionDependent: Koi.koi().enum(YesOrNoOrNotApplicable).allow(null),
    relevantHypoxemia: Koi.boolean().allow(null),
    remarkPositionDependent: getTextAreaStringSchema(true),
    remarkRelevantHypoxemia: getTextAreaStringSchema(true),
};

const osasConsultDraft: TypedOsasConsultSchema = {
    additionalAnamnesis: getTextAreaStringSchema(true),
    additionalConclusionChoice: Koi.koi().enum(YesOrNoOrOther).allow(null),
    additionalConclusionText: getTextAreaStringSchema(true),
    additionalPhysicalExamination: getTextAreaStringSchema(true),
    bmiCategory: Koi.koi().enum(BMICategories).allow(null),
    comorbidities: getTextAreaStringSchema(true),
    comorbiditiesFromReferral: comorbiditiesSchema.allow(null),
    comorbidityChoice: Koi.koi().enum(ComorbiditiesChoice).allow(null),
    consultDate: consultDateSchema,
    hasOsa: Koi.koi().enum(YesOrNoOrOther).allow(null),
    hasOsaOtherText: getTextAreaStringSchema(true),
    measureOfDiseaseBurden: Koi.koi().enum(MeasureOfCategory).allow(null),
    measureOfOsa: Koi.koi().enum(MeasureOfCategory).allow(null),
    ...osasConsultExaminationDraft,
    ...policyValidationDraft,
    ...intoxicationValidation,
};

const policyNoOsaFieldsFinal = {
    adviceGeneralPractitionerNoOsa: Koi.when('hasOsa', {
        is: YesOrNoOrOther.no,
        otherwise: Koi.string().allow(null),
        then: getTextAreaStringSchema(false),
    }),
    remarkAdviceGeneralPractitionerNoOsa: Koi.when('adviceGeneralPractitionerNoOsa', {
        is: 'Anders, namelijk...',
        otherwise: Koi.valid(null),
        then: getTextAreaStringSchema(false),
    }),
    remarkNoOsa: getTextAreaStringSchema(true),
};

const policyYesOsaFieldsFinal = {
    allTreatments: allTreatmentValidationFinal,
    hasTreatment: Koi.when('hasOsa', {
        is: YesOrNoOrOther.yes,
        otherwise: Koi.boolean().valid(null),
        then: Koi.boolean().required(),
    }),
    isDrivingCompetenceDiscussed: Koi.when('hasOsa', {
        is: YesOrNoOrOther.yes,
        otherwise: Koi.valid(null),
        then: Koi.koi().enum(YesOrNoOrNotApplicable).allow(null),
    }),
    remarkIsDrivingCompetenceDiscussed: getTextAreaStringSchema(true),
    remarkOther: getTextAreaStringSchema(true),
    ...policyHasTreatmentFieldsFinal,
    ...policyHasNoTreatmentFieldsFinal,
};

const policyValidationFinal = {
    ...policyYesOsaFieldsFinal,
    ...policyNoOsaFieldsFinal,
    ...therapielandProgramSelectionValidation,
};

export const osasConsultExaminationFinal = {
    T90Percent: getTextAreaStringSchema(true),
    centralSaGreaterThan10: Koi.boolean().allow(null),
    examinationDate: Koi.string().allow(null),
    otherRelevantFindings: Koi.object({
        frequentMovements: Koi.boolean(),
        indicationOfHeartRhythmDisorder: Koi.boolean(),
        inefficientSleepHygiene: Koi.boolean(),
        other: getTextAreaStringSchema(true),
        suboptimalSleepEfficiency: Koi.boolean(),
    }),
    positionDependent: Koi.koi().enum(YesOrNoOrNotApplicable).allow(null),
    relevantHypoxemia: Koi.boolean().allow(null),
    remarkPositionDependent: getTextAreaStringSchema(true),
    remarkRelevantHypoxemia: getTextAreaStringSchema(true),
};

const osasConsultFinal: TypedOsasConsultSchema = {
    additionalAnamnesis: getTextAreaStringSchema(true),
    additionalConclusionChoice: Koi.when('hasOsa', {
        is: YesOrNoOrOther.yes,
        otherwise: getTextAreaStringSchema(true),
        then: Koi.string().required(),
    }),
    additionalConclusionText: Koi.when('additionalConclusionChoice', {
        is: YesOrNoOrOther.yes,
        otherwise: getTextAreaStringSchema(true),
        then: getTextAreaStringSchema(false),
    }),
    additionalPhysicalExamination: getTextAreaStringSchema(true),
    bmiCategory: Koi.when('hasOsa', {
        is: YesOrNoOrOther.yes,
        otherwise: Koi.koi().enum(BMICategories).allow(null),
        then: Koi.koi().enum(BMICategories).required(),
    }),
    comorbidities: Koi.when('comorbidityChoice', {
        is: ComorbiditiesChoice.ja,
        otherwise: getTextAreaStringSchema(true),
        then: getTextAreaStringSchema(false),
    }),
    comorbiditiesFromReferral: comorbiditiesSchema.allow(null),
    comorbidityChoice: Koi.when('hasOsa', {
        is: YesOrNoOrOther.yes,
        otherwise: Koi.koi().enum(ComorbiditiesChoice).allow(null),
        then: Koi.koi().enum(ComorbiditiesChoice).required(),
    }),
    consultDate: consultDateSchema.required(),
    hasOsa: Koi.koi().enum(YesOrNoOrOther).required(),
    hasOsaOtherText: Koi.when('hasOsa', {
        is: YesOrNoOrOther.other,
        otherwise: Koi.string().valid(null),
        then: getTextAreaStringSchema(false),
    }),
    measureOfDiseaseBurden: Koi.when('hasOsa', {
        is: YesOrNoOrOther.yes,
        otherwise: Koi.koi().enum(MeasureOfCategory).allow(null),
        then: Koi.koi().enum(MeasureOfCategory).required(),
    }),
    measureOfOsa: Koi.when('hasOsa', {
        is: YesOrNoOrOther.yes,
        otherwise: Koi.koi().enum(MeasureOfCategory).allow(null),
        then: Koi.koi().enum(MeasureOfCategory).required(),
    }),
    ...osasConsultExaminationFinal,
    ...policyValidationFinal,
    ...intoxicationValidation,
};

export const osasConsultFinalSchema = Koi.object(osasConsultFinal).concat(watchPatSleepStudyPdfDataConsultSchema);

export const osasConsultDraftSchema = Koi.object(osasConsultDraft).concat(watchPatSleepStudyPdfDataConsultSchema);
